import { useState } from "react";
import { Boxes, ImageOff, RefreshCw } from "lucide-react";
import { Alert, Button, Dialog, Field, Input, Switch, useDialogs } from "../../../../ui";
import { normalizeSkuCode, SKU_MAX } from "../../shared/codes";
import { Combinations, previewSku } from "../combinations";
import { ProductForm, SkuDraft, draftFor } from "../editorModel";

export type CombinationsTableProps = {
  form: ProductForm;
  combos: Combinations;
  errorAt: (id: string) => string | null;
  onUpdateSku: (key: string, values: Partial<SkuDraft>) => void;
  onSetStockForAll: (stock: number) => void;
  onRebuild: () => void;
};

/** Editor §5: one row per SKU, grouped by style: on/off, SKU, stock (when tracking), price override. */
export function CombinationsTable({ form, combos, errorAt, onUpdateSku, onSetStockForAll, onRebuild }: CombinationsTableProps) {
  const { confirm } = useDialogs();
  const [stockOpen, setStockOpen] = useState(false);
  const [stockValue, setStockValue] = useState("");
  const productCode = form.code.trim();
  const enabledCount = combos.skus.filter((row) => draftFor(form, row.key).isEnabled).length;
  const stockNumber = Number(stockValue);
  const stockValid = stockValue.trim() !== "" && Number.isInteger(stockNumber) && stockNumber >= 0;
  const grouped = combos.styles.length > 1 || !!combos.styles[0]?.label;
  const sectionError = errorAt("section:combinations");

  if (combos.tooMany) {
    return (
      <section className="editor-section" data-target="section:combinations">
        <h3 className="editor-section__title">Combinations</h3>
        <Alert tone="warn">Remove some values or sizes to see the combinations.</Alert>
      </section>
    );
  }

  const handleRebuild = async () => {
    const example = combos.skus[0] ? previewSku(productCode, combos.skus[0].values, combos.skus[0].size) : "";
    const ok = await confirm({
      title: "Rebuild SKUs",
      tone: "primary",
      confirmLabel: "Rebuild SKUs",
      message: (
        <>
          Every SKU of this product is generated again from the current codes when you save
          {example ? (
            <>
              {" "}
              (e.g. <strong>{example}</strong>)
            </>
          ) : null}
          . Orders placed so far keep the SKU they were placed with.
        </>
      ),
    });
    if (ok) onRebuild();
  };

  const applyStock = () => {
    if (!stockValid) return;
    onSetStockForAll(stockNumber);
    setStockOpen(false);
  };

  return (
    <section className="editor-section" data-target="section:combinations">
      <div className="editor-section__head">
        <h3 className="editor-section__title">Combinations</h3>
        <div className="editor-section__actions">
          {form.trackStock && (
            <Button
              size="sm"
              variant="tint"
              icon={<Boxes size={14} />}
              disabled={enabledCount === 0}
              onClick={() => {
                setStockValue("");
                setStockOpen(true);
              }}
            >
              Set stock for all
            </Button>
          )}
          <Button size="sm" variant="ghost" icon={<RefreshCw size={14} />} onClick={handleRebuild} disabled={combos.skus.length === 0}>
            Rebuild SKUs
          </Button>
        </div>
      </div>
      <p className="editor-hint">
        Switch off the combinations that don't exist: customers never see them. A blank SKU is generated on save; a blank
        price uses the base price.
      </p>

      <div className={`combo-table${form.trackStock ? "" : " no-stock"}`} role="table" aria-label="Combinations">
        <div className="combo-row combo-row--head" role="row">
          <span role="columnheader">On</span>
          <span role="columnheader">Combination</span>
          <span role="columnheader">SKU</span>
          {form.trackStock && <span role="columnheader">Stock</span>}
          <span role="columnheader">Price</span>
        </div>
        {combos.styles.map((style) => {
          const rows = combos.skus.filter((row) => row.styleKey === style.key);
          const cover = form.styleImages[style.key]?.[0];
          return (
            <div key={style.key} className="combo-group" role="rowgroup">
              {grouped && (
                <div className="combo-group__head" role="row">
                  <span className="product-thumb">{cover ? <img src={cover} alt="" /> : <ImageOff size={14} />}</span>
                  <span className="combo-group__label">{style.label}</span>
                </div>
              )}
              {rows.map((row) => {
                const draft = draftFor(form, row.key);
                const error = errorAt(`sku:${row.key}`);
                const preview = previewSku(productCode, row.values, row.size);
                return (
                  <div
                    key={row.key}
                    data-target={`sku:${row.key}`}
                    className={`combo-row${draft.isEnabled ? "" : " is-off"}${error ? " has-error" : ""}`}
                    role="row"
                  >
                    <span
                      className="combo-row__on"
                      role="cell"
                      data-tooltip={draft.isEnabled ? "Switch off a combination that doesn't exist" : "Sell this combination"}
                    >
                      <Switch
                        checked={draft.isEnabled}
                        onChange={(isEnabled) => onUpdateSku(row.key, { isEnabled })}
                        label={`Sell ${row.label}`}
                        hideLabel
                      />
                    </span>
                    <span className="combo-row__label" role="cell">
                      {row.label}
                    </span>
                    <span className="combo-row__sku" role="cell">
                      <Input
                        inputSize="sm"
                        aria-label={`SKU for ${row.label}`}
                        value={draft.sku}
                        maxLength={SKU_MAX}
                        invalid={!!error}
                        placeholder={preview || "Generated on save"}
                        onChange={(e) => onUpdateSku(row.key, { sku: normalizeSkuCode(e.target.value, SKU_MAX) })}
                      />
                    </span>
                    {form.trackStock && (
                      <span className="combo-row__stock" role="cell">
                        <Input
                          inputSize="sm"
                          type="number"
                          min={0}
                          step={1}
                          aria-label={`Stock for ${row.label}`}
                          value={draft.stock}
                          onChange={(e) => onUpdateSku(row.key, { stock: e.target.value })}
                        />
                      </span>
                    )}
                    <span className="combo-row__price" role="cell">
                      <Input
                        inputSize="sm"
                        type="number"
                        min={0}
                        step="any"
                        aria-label={`Price for ${row.label}`}
                        value={draft.priceOverride}
                        placeholder={form.price || "Base price"}
                        rightSlot="EGP"
                        onChange={(e) => onUpdateSku(row.key, { priceOverride: e.target.value })}
                      />
                    </span>
                    {error && (
                      <p className="combo-row__error ui-field__error" role="alert">
                        {error}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {sectionError && <Alert tone="danger">{sectionError}</Alert>}

      <Dialog
        open={stockOpen}
        onClose={() => setStockOpen(false)}
        title="Set stock for all"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setStockOpen(false)}>
              Cancel
            </Button>
            <Button onClick={applyStock} disabled={!stockValid}>
              Apply to {enabledCount} {enabledCount === 1 ? "SKU" : "SKUs"}
            </Button>
          </>
        }
      >
        <Field label="Stock" required hint="Every switched-on SKU gets this stock. It's saved when you save the product.">
          {(id) => (
            <Input
              id={id}
              type="number"
              min={0}
              step={1}
              value={stockValue}
              placeholder="0"
              onChange={(e) => setStockValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyStock();
              }}
            />
          )}
        </Field>
      </Dialog>
    </section>
  );
}

export default CombinationsTable;
